import ReactDOM from 'react-dom/client';
import { lazy, StrictMode, Suspense } from 'react';
import GlobalLoading from './GlobalLoading';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import AppErrorBoundary from './AppErrorBoundary';
import { version } from '../version';
import RegisterSW from './RegisterSW';
import { AccountRole } from '@football-kids/shared/enums';
import { noiseErrorsArray } from '@football-kids/shared/utils/sentry';
import { sharedEnv } from '@football-kids/shared/utils/env';
import { TenantContext } from './context/TenantContext';
import { getEnv } from './utils/hooks';

import { MedusaContextProvider } from '@football-kids/inventory-client';

// if (!sharedEnv('DEV')) {
    const sentryDsn = getEnv('VITE_SENTRY_DSN');
    console.log('sentryDsn', sentryDsn);
    if (sentryDsn) {
        import('@sentry/react').then((Sentry) => {
            (window as any).Sentry = Sentry;

            Sentry.init({
                dsn: sentryDsn,
                release: version,
                environment: sharedEnv('VITE_SENTRY_ENV'),
                tracesSampleRate: Number(sharedEnv('VITE_SENTRY_SAMPLE_RATE')),
                integrations: [
                    Sentry.browserTracingIntegration({}),
                    Sentry.extraErrorDataIntegration({
                        captureErrorCause: true,
                    }),
                ],
                ...(sharedEnv('VITE_PWA_API_REGEX') && {
                    tracePropagationTargets: [
                        new RegExp(sharedEnv('VITE_PWA_API_REGEX'))
                    ]
                }),
                ignoreErrors: noiseErrorsArray,
                beforeSend(event, hint) {
                    // Check if it is an exception, and if so, show the report dialog
                    if (
                        event.exception &&
                        event.event_id &&
                        [
                            AccountRole.ADMIN,
                            AccountRole.MANAGER,
                            AccountRole.COACH,
                        ].includes(event.contexts?.account?.role as any)
                    ) {
                        Sentry.showReportDialog({
                            eventId: event.event_id,

                            title: `Wysyłanie raportu`,
                            subtitle: `Jeśli masz życzenie nam pomóc, możesz opisać sytuację która doprowadziła do tego błędu.`,
                            subtitle2: `Dziękujemy za twoją pomoc`,
                            labelName: 'Imię (Opcjonalne)',
                            labelEmail: `Email (Opcjonalne)`,
                            labelComment: 'Co się stało',
                            labelClose: 'Zamknij',
                            labelSubmit: 'Wyślij raport',
                            onClose() {
                                window.location.reload();
                            },
                        });
                    }
                    return event;
                },
            });
        });
    }
//}

console.log(`Wersja aplikacji: ${version}`);

const startTime = performance.now();
console.log('startTime', startTime);

const App = lazy(() => {
    console.log('appLoadingStartTime', performance.now());

    return import('./App')
        .catch((e, ...args) => {
            console.log('appLoadingCatch', performance.now());

            console.log(e);
            console.log(args);

            if ((window as any)?.Sentry) {
                (window as any)?.Sentry.captureException(
                    new Error('app loading catch')
                );
            }

            return { default: GlobalLoading };
        })
        .finally(() => {
            if (startTime) {
                console.log('appLoadingEndTime', performance.now());
                const duration = performance.now() - startTime;

                if (window.Sentry && duration > 60 * 30) {
                    window.Sentry?.setContext?.('connection', {
                        loadingDuration: duration,
                        downlink: navigator.connection?.downlink,
                        effectiveType: navigator.connection?.effectiveType,
                    });
                    window?.Sentry.captureException(
                        new Error('app loading too long')
                    );

                    if (
                        navigator?.connection?.downlink &&
                        navigator?.connection?.downlink > 3
                    ) {
                        window.location.reload(true);
                    }
                }
            }
        });
});

const router = createBrowserRouter([
    {
        path: '*',
        element: <App />,
        errorElement: <div>
            Wystąpił błąd. Spróbuj odświeżyć stronę i skontaktuj
            się z administratorem
        </div>
    },
]);

const main = ({ theme, globalStyles }: any) => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
        <StrictMode>
            <RegisterSW />
            <Suspense fallback={<GlobalLoading />}>
                <AppErrorBoundary
                    fallback={
                        <div>
                            Wystąpił błąd. Spróbuj odświeżyć stronę i skontaktuj
                            się z administratorem
                        </div>
                    }
                >
                    <TenantContext.Provider value={{ theme, globalStyles }}>
                        <MedusaContextProvider>
                            <RouterProvider router={router} />
                        </MedusaContextProvider>
                    </TenantContext.Provider>
                </AppErrorBoundary>
            </Suspense>
        </StrictMode>
    );
};

export default main;
